import React, { useMemo, useState } from 'react';
import { addDays, format, isSameDay, isWeekend, startOfToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CheckCircle2Icon, ClockIcon, MinusIcon, PlusIcon, UsersIcon } from 'lucide-react';
import { slotsByDay, visitTypes } from '../data/visits';
import { formatPrice } from '../contexts/CartContext';
const visitImage = 'https://cdn.magicpatterns.com/patterns/generated-images/95e2060a-69f0-40b8-9c2c-3aee21fac792.jpg';
export function Visites() {
  const today = startOfToday();
  const days = useMemo(() => Array.from({
    length: 14
  }, (_, index) => addDays(today, index + 1)).filter(day => day.getDay() !== 0 && day.getDay() !== 1), [today]);
  const [visitId, setVisitId] = useState(visitTypes[0].id);
  const [date, setDate] = useState<Date>(days[0]);
  const [time, setTime] = useState<string | null>(null);
  const [people, setPeople] = useState(2);
  const [confirmed, setConfirmed] = useState(false);
  const visit = visitTypes.find(item => item.id === visitId) ?? visitTypes[0];
  const slots = isWeekend(date) ? slotsByDay.weekend : slotsByDay.default;
  const selectedSlot = slots.find(slot => slot.time === time);
  const total = visit.price * people;
  const selectDate = (day: Date) => {
    setDate(day);
    setTime(null);
  };
  if (confirmed) {
    return <div className="mx-auto max-w-2xl px-5 py-24 text-center lg:px-8" data-id="element-260" data-mp="2d1amh7vufd1n">
        <CheckCircle2Icon className="mx-auto h-10 w-10 text-olive" aria-hidden="true" data-id="element-261" data-mp="39o6xpiwuxlfm" />
        <h1 className="mt-5 font-display text-3xl text-ink" data-id="element-262" data-mp="35oy4cf5qbpkl">Réservation enregistrée</h1>
        <p className="mt-4 text-base leading-relaxed text-muted" data-id="element-263" data-mp="30wd9rbf018g0">
          {visit.name} le {format(date, 'EEEE d MMMM', {
          locale: fr
        })} à {time}, pour {people}{' '}
          personne{people > 1 ? 's' : ''}. Un e-mail de confirmation avec le plan d’accès vient de vous
          être envoyé.
        </p>
        <p className="mt-2 text-sm text-muted" data-id="element-264" data-mp="3olpun8jov4y5">
          Le règlement se fait sur place. Prévoyez des chaussures fermées.
        </p>
        <button type="button" onClick={() => {
        setConfirmed(false);
        setTime(null);
      }} className="mt-8 rounded-full border border-ink px-5 py-3 text-sm font-medium text-ink transition-colors duration-150 ease-soft hover:bg-ink hover:text-cream" data-id="element-265" data-mp="bedjy6w630d4">
          Réserver une autre visite
        </button>
      </div>;
  }
  return <div data-id="element-266" data-mp="3or70og0o2kdd">
      <div className="relative" data-id="element-267" data-mp="3ouusnaqgqypz">
        <img src={visitImage} alt="Démonstration de tonte et de feutrage dans la bergerie" className="h-[38vh] min-h-[280px] w-full object-cover" data-id="element-268" data-mp="3ts4fcimgwwa4" />
        <div className="absolute inset-0 bg-ink/45" data-id="element-269" data-mp="3jt5ehm7y24xo" />
        <div className="absolute inset-0 flex items-end" data-id="element-270" data-mp="3jtx9autpvymw">
          <div className="mx-auto w-full max-w-page px-5 pb-10 lg:px-8" data-id="element-271" data-mp="1dzxewbijwhwt">
            <h1 className="max-w-2xl font-display text-4xl leading-tight text-cream sm:text-5xl" data-id="element-272" data-mp="1i222otmbikbj">
              Réserver une visite à la ferme
            </h1>
            <p className="mt-4 max-w-xl text-base text-cream/85" data-id="element-273" data-mp="32kmc8awrsjjg">
              Du mardi au samedi, Audrey ouvre la bergerie et l’atelier. Groupes de 15 personnes maximum.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-page gap-10 px-5 py-14 lg:grid-cols-[1.6fr_1fr] lg:px-8" data-id="element-274" data-mp="3cbqsrf23gcge">
        <div className="space-y-12" data-id="element-275" data-mp="37hcsozchlh5e">
          <section aria-labelledby="etape-1" data-id="element-276" data-mp="3m7ampvx3l12i">
            <Step number={1} id="etape-1" title="Choisir la visite" data-id="element-277" data-mp="3ma6k9j519nqf" />
            <div className="mt-5 grid gap-4 sm:grid-cols-3" data-id="element-278" data-mp="3h87ey3pqggqu">
              {visitTypes.map(item => {
              const isActive = item.id === visitId;
              return <button key={item.id} type="button" onClick={() => setVisitId(item.id)} aria-pressed={isActive} className={`flex h-full flex-col rounded-lg border p-5 text-left transition-colors duration-150 ease-soft ${isActive ? 'border-olive bg-parchment' : 'border-line hover:border-ink'}`} data-id="element-279" data-mp="3q9fa4cwyd1gp">
                    <h3 className="font-display text-lg leading-snug text-ink" data-id="element-280" data-mp="27azlpm77w6el">{item.name}</h3>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-muted" data-id="element-281" data-mp="2796eo5sozk62">
                      <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-282" data-mp="2c7zptrkiaz32" />
                      {item.duration} · {item.season}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-283" data-mp="1jmgcn4gcfplg">{item.description}</p>
                    <span className="mt-auto pt-4 text-sm font-medium text-ink" data-id="element-284" data-mp="1ok86i40jtlsa">
                      {formatPrice(item.price)} / personne
                    </span>
                  </button>;
            })}
            </div>
          </section>

          <section aria-labelledby="etape-2" data-id="element-285" data-mp="10uxbz49sknml">
            <Step number={2} id="etape-2" title="Choisir la date" data-id="element-286" data-mp="10tm7t3cfpduy" />
            <div className="mt-5 flex gap-2 overflow-x-auto pb-2" data-id="element-287" data-mp="tb4ltxndg6l3">
              {days.map(day => {
              const isActive = isSameDay(day, date);
              return <button key={day.toISOString()} type="button" onClick={() => selectDate(day)} aria-pressed={isActive} className={`flex w-[4.5rem] shrink-0 flex-col items-center rounded-lg border py-3 transition-colors duration-150 ease-soft ${isActive ? 'border-ink bg-ink text-cream' : 'border-line text-ink hover:border-ink'}`} data-id="element-288" data-mp="jd88cz6yozjt">
                    <span className={`text-[11px] uppercase ${isActive ? 'text-cream/70' : 'text-muted'}`} data-id="element-289" data-mp="3rt1rllsogp4k">
                      {format(day, 'EEE', {
                    locale: fr
                  })}
                    </span>
                    <span className="mt-1 font-display text-xl leading-none" data-id="element-290" data-mp="3mvrvjnas9f7r">{format(day, 'd')}</span>
                    <span className={`mt-1 text-[11px] ${isActive ? 'text-cream/70' : 'text-muted'}`} data-id="element-291" data-mp="387877w252ps0">
                      {format(day, 'MMM', {
                    locale: fr
                  })}
                    </span>
                  </button>;
            })}
            </div>

            <h3 className="mt-6 text-sm font-medium text-ink" data-id="element-292" data-mp="2z84r90y9qqhq">
              Créneaux du {format(date, 'EEEE d MMMM', {
              locale: fr
            })}
            </h3>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-293" data-mp="3rupf3rfjckgj">
              {slots.map(slot => {
              const full = slot.places === 0;
              const isActive = slot.time === time;
              return <button key={slot.time} type="button" disabled={full} onClick={() => setTime(slot.time)} aria-pressed={isActive} className={`rounded-full border px-4 py-2.5 text-sm transition-colors duration-150 ease-soft ${isActive ? 'border-olive bg-olive text-cream' : 'border-line text-ink hover:border-ink'} disabled:cursor-not-allowed disabled:border-line disabled:text-line disabled:hover:border-line`} data-id="element-294" data-mp="3i4cygaaudbfo">
                    {slot.time}
                    <span className={`ml-2 text-xs ${isActive ? 'text-cream/75' : 'text-muted'}`} data-id="element-295" data-mp="xkfsqo132e2e">
                      {full ? 'complet' : `${slot.places} places`}
                    </span>
                  </button>;
            })}
            </div>
          </section>

          <section aria-labelledby="etape-3" data-id="element-296" data-mp="jt9mm93ati6j">
            <Step number={3} id="etape-3" title="Nombre de participants" data-id="element-297" data-mp="jucfgmk6lup3" />
            <div className="mt-5 flex items-center gap-4" data-id="element-298" data-mp="eu6q5d1dwka6">
              <div className="flex items-center rounded-full border border-line" data-id="element-299" data-mp="ewsy3ds6t1sx">
                <button type="button" onClick={() => setPeople(value => Math.max(1, value - 1))} className="p-3 text-muted transition-colors duration-150 ease-soft hover:text-ink" aria-label="Retirer un participant" data-id="element-300" data-mp="1vasw2j7h2hdj">
                  <MinusIcon className="h-4 w-4" data-id="element-301" data-mp="3op2mbw8fgmsi" />
                </button>
                <span className="w-10 text-center font-display text-lg tabular-nums" data-id="element-302" data-mp="3toytg4cf8uca">{people}</span>
                <button type="button" onClick={() => setPeople(value => Math.min(15, value + 1))} className="p-3 text-muted transition-colors duration-150 ease-soft hover:text-ink" aria-label="Ajouter un participant" data-id="element-303" data-mp="2df5hmre1u9y">
                  <PlusIcon className="h-4 w-4" data-id="element-304" data-mp="gceyf6lxovbx" />
                </button>
              </div>
              <p className="flex items-center gap-1.5 text-sm text-muted" data-id="element-305" data-mp="l9x66to7t4ic">
                <UsersIcon className="h-4 w-4" aria-hidden="true" data-id="element-306" data-mp="2i41nh2m9mg5r" />
                Gratuit pour les moins de 6 ans
              </p>
            </div>
            {selectedSlot && people > selectedSlot.places && <p className="mt-3 text-sm text-clay" data-id="element-307" data-mp="2fk505rwt3ki8">
                Il ne reste que {selectedSlot.places} places sur ce créneau — choisissez-en un autre ou
                réduisez le nombre de participants.
              </p>}
          </section>
        </div>

        <aside className="h-fit rounded-lg border border-line bg-parchment p-6 lg:sticky lg:top-24" data-id="element-308" data-mp="20ufov3v02048">
          <h2 className="font-display text-xl text-ink" data-id="element-309" data-mp="2ygda390rkjjv">Votre réservation</h2>
          <dl className="mt-5 space-y-3 text-sm" data-id="element-310" data-mp="2thtnr7i9d5qz">
            <Row label="Visite" value={visit.name} data-id="element-311" data-mp="2tj4pl1s583fg" />
            <Row label="Date" value={format(date, 'EEEE d MMMM', {
            locale: fr
          })} data-id="element-312" data-mp="2oipcmbbrwxy0" />
            <Row label="Créneau" value={time ?? '—'} muted={!time} data-id="element-313" data-mp="2khx4jlen0z3b" />
            <Row label="Participants" value={`${people}`} data-id="element-314" data-mp="2kjq28b79w8z6" />
            <Row label="Durée" value={visit.duration} data-id="element-315" data-mp="1d54sdykomrvw" />
          </dl>
          <div className="mt-5 flex items-baseline justify-between border-t border-line pt-5" data-id="element-316" data-mp="192q95dd3pq43">
            <span className="text-sm text-muted" data-id="element-317" data-mp="20kqghaxx4erh">Total sur place</span>
            <span className="font-display text-2xl text-ink tabular-nums" data-id="element-318" data-mp="20ix7vq3qvkgu">{formatPrice(total)}</span>
          </div>
          <button type="button" disabled={!time || (selectedSlot ? people > selectedSlot.places : true)} onClick={() => setConfirmed(true)} className="mt-5 w-full rounded-full bg-olive py-3 text-sm font-medium text-cream transition-colors duration-150 ease-soft hover:bg-olive-dark disabled:cursor-not-allowed disabled:bg-line disabled:text-cream" data-id="element-319" data-mp="2aat6d29qtcll">
            {time ? 'Confirmer la réservation' : 'Choisissez un créneau'}
          </button>
          <p className="mt-3 text-xs leading-relaxed text-muted" data-id="element-320" data-mp="2oakqzlurlr8k">
            Règlement sur place, annulation gratuite jusqu’à 24 h avant. La visite est maintenue par
            temps de pluie.
          </p>
        </aside>
      </div>
    </div>;
}
function Step({
  number,
  title,
  id
}: {
  number: number;
  title: string;
  id: string;
}) {
  return <div className="flex items-center gap-3 border-b border-line pb-3" data-id="element-321" data-mp="35l7ut8gd7zjw">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink text-xs font-semibold text-cream" data-id="element-322" data-mp="3ajj33er0i200">
        {number}
      </span>
      <h2 id={id} className="font-display text-xl text-ink" data-id="element-323" data-mp="2wrcq21j5ob4s">
        {title}
      </h2>
    </div>;
}
function Row({
  label,
  value,
  muted = false
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return <div className="flex items-baseline justify-between gap-4" data-id="element-324" data-mp="2mvrtiiwuw32f">
      <dt className="text-muted" data-id="element-325" data-mp="2rtb5td9zu5rq">{label}</dt>
      <dd className={`text-right font-medium ${muted ? 'text-muted' : 'text-ink'}`} data-id="element-326" data-mp="2dsqnsjbwkdza">{value}</dd>
    </div>;
}